import { configureStore, combineReducers } from '@reduxjs/toolkit';
import { persistStore, persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';

import confirmationReducer from './slices/confirmationSlice';
import gameReducer from './slices/gameSlice';
import notificationReducer from './slices/notificationSlice';
import productReducer from './slices/productSlice';
import settingsReducer from './slices/settingsSlice';

// Flag to track if Game page has been opened
let gamePageOpened = false;

// Custom storage that only persists after Game page is opened
const conditionalStorage = {
  ...storage,
  setItem: (key: string, value: string) => {
    if (gamePageOpened) {
      return storage.setItem(key, value);
    }
    return Promise.resolve();
  },
};

// Redux-persist configuration
const persistConfig = {
  key: 'playmerce-root',
  storage: conditionalStorage,
  whitelist: ['game', 'products'], // Only persist game and products state
  // blacklist: ['notification'], // Don't persist notification state (already excluded by whitelist)
};

// Game slice persist configuration (more specific control)
const gamePersistConfig = {
  key: 'game',
  storage: conditionalStorage,
  whitelist: [
    'claimedPrizes',
    'walletAmount',
    'claimedPrizeIds',
    'appState',
    'selectedProduct',
    'distributedItems',
    'hasShownSelectProductNotification',
  ], // Don't persist loading state or items (will be regenerated)
};

// Product slice persist configuration
const productPersistConfig = {
  key: 'products',
  storage: conditionalStorage,
  whitelist: ['cachedProducts'],
};

// Combine reducers with persistence
const rootReducer = combineReducers({
  game: persistReducer(gamePersistConfig, gameReducer),
  products: persistReducer(productPersistConfig, productReducer),
  confirmation: confirmationReducer, // Don't persist confirmation state
  notification: notificationReducer, // Don't persist notification state
  settings: settingsReducer, // Don't persist settings state (fetched fresh each session)
});

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ['persist/PERSIST', 'persist/REHYDRATE', 'persist/PAUSE', 'persist/PURGE', 'persist/REGISTER'],
      },
    }),
});

export const persistor = persistStore(store);

// Function to enable persistence when Game page is opened
export const enablePersistence = () => {
  gamePageOpened = true;
};

// Function to check if persistence is enabled
export const isPersistenceEnabled = () => {
  return gamePageOpened;
};

// Create types based on the non-persisted reducer for easier type access
type AppState = ReturnType<typeof rootReducer>;
export type RootState = AppState;
export type AppDispatch = typeof store.dispatch;
